import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { TechnologyForm } from '../../components/technologies';
import { createTechnology, ApiError, type CreateTechnologyInput } from '../../lib/apiClient';
import type { CreateTechnologyFormData } from '../../components/forms/validation';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './new.css';

export const Route = createFileRoute('/technologies/new')({
  component: CreateTechnologyPage,
});

export { CreateTechnologyPage };

function CreateTechnologyPage(): JSX.Element {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateTechnologyFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const input: CreateTechnologyInput = {
        name: data.name,
        description: data.description || undefined,
        productFamilyId: data.productFamilyId || undefined,
        version: data.version || undefined,
        lifecycleStage: data.lifecycleStage || undefined,
        supplierId: data.supplierId || undefined,
        licenseType: (data.licenseType as string | undefined) || undefined,
        supportEndDate: data.supportEndDate || undefined,
        securityRating: (data.securityRating as string | undefined) || undefined,
        notes: data.notes || undefined,
      };
      const technology = await createTechnology(input);
      void navigate({
        to: '/technologies/$technologyId',
        params: { technologyId: technology.entityId },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({
      to: '/technologies',
      search: { page: 1, pageSize: 25, supplierId: '', productFamilyId: '' },
    });
  };

  return (
    <div className="create-technology-page">
      <div className="breadcrumb">
        <Link
          to="/technologies"
          search={{ page: 1, pageSize: 25, supplierId: '', productFamilyId: '' }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Technologies</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Technology</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new technology to the EA catalog
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TechnologyForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
